import React, { useEffect, useState } from "react";

const StockList: React.FC = () => {
  const [groupedStocks, setGroupedStocks] = useState<{
    [key: string]: string[];
  }>({});

  // Fetch and group stocks
  useEffect(() => {
    fetch("/ticker_list.csv") // Update path if needed
      .then((response) => response.text())
      .then((data) => {
        const rows = data
          .split("\n")
          .map((row) => row.trim())
          .filter((row) => row);

        // Group stocks by first letter
        const grouped: { [key: string]: string[] } = {};
        rows.forEach((stock) => {
          const firstLetter = stock[0].toUpperCase();
          if (!grouped[firstLetter]) {
            grouped[firstLetter] = [];
          }
          grouped[firstLetter].push(stock);
        });

        setGroupedStocks(grouped);
      })
      .catch((error) => console.error("Error loading stock list:", error));
  }, []);

  // Alphabet links
  const alphabet = "ABCDEFGHIJKLMNOPQRSTUVWXYZ".split("");

  useEffect(() => {
    const handleAnchorClick = (e: Event) => {
      const link = e.target as HTMLAnchorElement;
      e.preventDefault();
      const targetId = link.getAttribute("href")?.substring(1);
      if (targetId) {
        const target = document.getElementById(targetId);
        target?.scrollIntoView({ behavior: "auto" });
      }
    };

    // Disable smooth scrolling for anchor links
    const anchorLinks = document.querySelectorAll('a[href^="#"]');
    anchorLinks.forEach((link) => {
      link.addEventListener("click", handleAnchorClick);
    });

    // Cleanup the event listeners on unmount
    return () => {
      anchorLinks.forEach((link) => {
        link.removeEventListener("click", handleAnchorClick);
      });
    };
  }, []);

  return (
    <div className="min-h-screen bg-background py-10 px-6">
      <div className="fixed left-0 top-16 p-6 bg-background w-full">
        <h1 className="text-center text-2xl md:text-3xl font-serif text-white my-6">
          Stock List
        </h1>

        {/* Sticky Alphabet Index */}
        <div className="sticky top-0 z-10 shadow-sm py-2 mb-6 bg-background">
          <div className="flex flex-wrap justify-center overflow-x-auto whitespace-nowrap px-2 sm:px-6">
            {alphabet.map((letter) => (
              <a
                key={letter}
                href={`#${letter}`}
                className="text-blue-500 underline hover:text-white hover:bg-blue-500 hover:rounded-md px-2 py-1 mx-1 sm:px-3 transition duration-200 ease-in-out"
              >
                {letter}
              </a>
            ))}
          </div>
        </div>
      </div>

      {/* Stock List */}
      <div className="mt-60 lg:mt-48 px-4 sm:px-8 lg:px-44 z-0">
        {alphabet.map((letter) => (
          <div key={letter} id={letter} className="mt-8 scroll-mt-72">
            <h2 className="text-xl sm:text-2xl font-serif text-white mb-4 pt-10">
              {letter}
            </h2>
            <ul className="text-white/70 space-y-2">
              {groupedStocks[letter]?.length > 0 ? (
                groupedStocks[letter].map((stock, index) => (
                  <li key={index} className="border-b py-2">
                    {stock}
                  </li>
                ))
              ) : (
                <li className="text-gray-500 italic">
                  No stocks starting with {letter}
                </li>
              )}
            </ul>
          </div>
        ))}
      </div>
    </div>
  );
};

export default StockList;
